// Local storage utilities for couple authentication

import { 
  CoupleAuthData, 
  StoredCoupleAuth, 
  STORAGE_KEYS, 
  TOKEN_CONFIG,
  AuthenticationError 
} from '../../types/authentication';

/**
 * Store couple authentication data in localStorage
 */
export function storeCoupleAuth(authData: CoupleAuthData): void {
  try {
    const storageData = {
      id: authData.id,
      partner1_name: authData.partner1_name,
      partner2_name: authData.partner2_name,
      access_token: authData.access_token,
      expires_at: authData.expires_at,
      stored_at: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEYS.COUPLE_AUTH, JSON.stringify(storageData));
    localStorage.setItem(STORAGE_KEYS.LAST_LOGIN, new Date().toISOString());
    
    // Set session expiry for easier checking
    localStorage.setItem(STORAGE_KEYS.SESSION_EXPIRY, authData.expires_at);
  } catch (error) {
    console.error('Failed to store couple auth data:', error);
    throw new Error('Failed to save authentication data');
  }
}

/**
 * Retrieve couple authentication data from localStorage
 */
export function getStoredCoupleAuth(): StoredCoupleAuth | null {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.COUPLE_AUTH);
    
    if (!storedData) {
      return null;
    }

    const parsedData = JSON.parse(storedData);
    const expiresAt = new Date(parsedData.expires_at);
    const now = new Date();
    const isValid = expiresAt > now;

    return {
      id: parsedData.id,
      partner1_name: parsedData.partner1_name,
      partner2_name: parsedData.partner2_name,
      access_token: parsedData.access_token,
      expires_at: parsedData.expires_at,
      is_valid: isValid
    };
  } catch (error) {
    console.error('Failed to retrieve stored auth data:', error);
    return null;
  }
}

/**
 * Check if stored authentication data is still valid
 */
export function isAuthenticationValid(): boolean {
  try {
    const storedAuth = getStoredCoupleAuth();
    
    if (!storedAuth) {
      return false;
    }

    return storedAuth.is_valid;
  } catch (error) {
    console.error('Failed to validate authentication:', error);
    return false;
  }
}

/**
 * Check if authentication token needs refreshing
 */
export function shouldRefreshToken(): boolean {
  try {
    const storedAuth = getStoredCoupleAuth();
    
    if (!storedAuth || !storedAuth.is_valid) {
      return false;
    }

    const expiresAt = new Date(storedAuth.expires_at);
    const now = new Date();
    const hoursUntilExpiry = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60);

    return hoursUntilExpiry <= TOKEN_CONFIG.REFRESH_THRESHOLD_HOURS;
  } catch (error) {
    console.error('Failed to check token refresh status:', error);
    return false;
  }
}

/**
 * Clear stored authentication data (logout)
 */
export function clearStoredAuth(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.COUPLE_AUTH);
    localStorage.removeItem(STORAGE_KEYS.LAST_LOGIN);
    localStorage.removeItem(STORAGE_KEYS.SESSION_EXPIRY);
  } catch (error) {
    console.error('Failed to clear stored auth data:', error);
  }
}

/**
 * Get the current user's partner role based on login
 */
export function getCurrentPartnerRole(loginPartnerName: string): 'partner1' | 'partner2' | null {
  try {
    const storedAuth = getStoredCoupleAuth();
    
    if (!storedAuth) {
      return null;
    }

    if (storedAuth.partner1_name.toLowerCase() === loginPartnerName.toLowerCase()) {
      return 'partner1';
    } else if (storedAuth.partner2_name.toLowerCase() === loginPartnerName.toLowerCase()) {
      return 'partner2';
    }

    return null;
  } catch (error) {
    console.error('Failed to determine partner role:', error);
    return null;
  }
}

/**
 * Get both partner names from stored auth
 */
export function getPartnerNames(): { partner1_name: string; partner2_name: string } | null {
  try {
    const storedAuth = getStoredCoupleAuth();
    
    if (!storedAuth) {
      return null;
    }

    return {
      partner1_name: storedAuth.partner1_name,
      partner2_name: storedAuth.partner2_name
    };
  } catch (error) {
    console.error('Failed to get partner names:', error);
    return null;
  }
}

/**
 * Get last login timestamp
 */
export function getLastLoginTime(): Date | null {
  try {
    const lastLogin = localStorage.getItem(STORAGE_KEYS.LAST_LOGIN);
    
    if (!lastLogin) {
      return null;
    }

    return new Date(lastLogin);
  } catch (error) {
    console.error('Failed to get last login time:', error);
    return null;
  }
}

/**
 * Update stored auth with new expiry time (for token refresh)
 */
export function updateAuthExpiry(newExpiryTime: string): void {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.COUPLE_AUTH);
    
    if (!storedData) {
      throw new Error('No stored auth data to update');
    }

    const parsedData = JSON.parse(storedData);
    parsedData.expires_at = newExpiryTime;

    localStorage.setItem(STORAGE_KEYS.COUPLE_AUTH, JSON.stringify(parsedData));
    localStorage.setItem(STORAGE_KEYS.SESSION_EXPIRY, newExpiryTime);
  } catch (error) {
    console.error('Failed to update auth expiry:', error);
    throw new Error('Failed to update authentication expiry');
  }
}